import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { pdf, Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';

const VatReport = () => {
  const [invoices, setInvoices] = useState([]);
  const [purchaseInvoices, setPurchaseInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Date range states - default to full year
  const [dateFrom, setDateFrom] = useState(() => {
    const today = new Date();
    const yearStart = new Date(today.getFullYear(), 0, 1);
    // Format as YYYY-MM-DD without timezone conversion
    const year = yearStart.getFullYear();
    const month = String(yearStart.getMonth() + 1).padStart(2, '0');
    const day = String(yearStart.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });
  const [dateTo, setDateTo] = useState(() => {
    const today = new Date();
    // Format as YYYY-MM-DD without timezone conversion
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });
  
  // Tab state
  const [activeTab, setActiveTab] = useState('invoices');
  
  // VAT calculations
  const [vatPayable, setVatPayable] = useState(0);
  const [vatReceivable, setVatReceivable] = useState(0);
  const [netVat, setNetVat] = useState(0);

  const apiUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  // Parse date string to compare dates correctly (avoids timezone issues)
  const parseDate = (dateString) => {
    if (!dateString) return new Date(1970, 0, 1);
    // Handle ISO date strings and YYYY-MM-DD format
    const match = dateString.match(/(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const [, year, month, day] = match;
      // Create date at midnight local time
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day), 0, 0, 0, 0);
    }
    // Fallback to standard parsing
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return new Date(1970, 0, 1);
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
  };

  // Check if date is in range
  const isDateInRange = (dateString, fromStr, toStr) => {
    const checkDate = parseDate(dateString);
    const fromDate = parseDate(fromStr);
    const toDate = parseDate(toStr);
    toDate.setHours(23, 59, 59, 999);
    
    return checkDate >= fromDate && checkDate <= toDate;
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  // Calculate VAT from invoice
  const calculateInvoiceVAT = (invoice) => {
    if (!invoice) {
      console.log('  ⚠️  calculateInvoiceVAT: invoice is null/undefined');
      return 0;
    }
    
    // For purchase invoices (plines)
    if (invoice.plines && Array.isArray(invoice.plines) && invoice.plines.length > 0) {
      const vat = invoice.plines.reduce((sum, line) => {
        const lineVat = parseFloat(line.line_vat || 0);
        console.log(`    Line VAT: ${lineVat}`);
        return sum + lineVat;
      }, 0);
      console.log(`  💰 PO ${invoice.id} (${invoice.reference_no}): plines count=${invoice.plines.length}, calculated VAT=${vat}`);
      return vat;
    }
    
    // For sales invoices (ilines)
    if (invoice.ilines && Array.isArray(invoice.ilines) && invoice.ilines.length > 0) {
      const vat = invoice.ilines.reduce((sum, line) => {
        return sum + parseFloat(line.line_vat || 0);
      }, 0);
      console.log(`  💰 Invoice ${invoice.id}: ilines count=${invoice.ilines.length}, calculated VAT=${vat}`);
      return vat;
    }

    // Fallback: try other line item property names
    const lines = invoice.items || [];
    if (!lines.length) {
      console.log(`  ⚠️  Invoice ${invoice.id}: no lines found (plines=${invoice.plines}, ilines=${invoice.ilines}, items=${invoice.items})`);
      return 0;
    }

    return lines.reduce((sum, line) => {
      if (line.line_vat) {
        return sum + parseFloat(line.line_vat || 0);
      }
      
      // Calculate if not available
      const quantity = parseFloat(line.quantity || 0);
      const unitPrice = parseFloat(line.unit_price || line.unitPrice || 0);
      const discount = parseFloat(line.discount_amount || line.discountAmount || 0);
      const vatPercent = parseFloat(line.vat_percent || line.vatPercent || 0);
      const afterDiscount = quantity * unitPrice - discount;
      const vat = afterDiscount * (vatPercent / 100);
      return sum + vat;
    }, 0);
  };

  // Calculate total from invoice
  const calculateInvoiceTotal = (invoice) => {
    if (!invoice) return 0;
    
    // For purchase invoices (plines)
    if (invoice.plines && Array.isArray(invoice.plines)) {
      return invoice.plines.reduce((sum, line) => {
        return sum + parseFloat(line.line_total || 0);
      }, 0);
    }
    
    // For sales invoices (ilines)
    if (invoice.ilines && Array.isArray(invoice.ilines)) {
      return invoice.ilines.reduce((sum, line) => {
        return sum + parseFloat(line.line_total || 0);
      }, 0);
    }

    // If total exists directly, use it
    if (invoice.total) return parseFloat(invoice.total);
    if (invoice.line_total) return parseFloat(invoice.line_total);
    
    // Fallback: try other line item property names
    const lines = invoice.items || [];
    if (!lines.length) return 0;

    return lines.reduce((sum, line) => {
      if (line.line_total) {
        return sum + parseFloat(line.line_total || 0);
      }
      
      // Calculate it
      const quantity = parseFloat(line.quantity || 0);
      const unitPrice = parseFloat(line.unit_price || line.unitPrice || 0);
      const discount = parseFloat(line.discount_amount || line.discountAmount || 0);
      const vatPercent = parseFloat(line.vat_percent || line.vatPercent || 0);
      const subtotal = quantity * unitPrice;
      const afterDiscount = subtotal - discount;
      const vat = afterDiscount * (vatPercent / 100);
      const total = afterDiscount + vat;
      return sum + total;
    }, 0);
  };

  // Fetch invoices and purchase invoices
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [invoicesRes, purchaseRes] = await Promise.all([
          axios.get(`${apiUrl}/api/invoices`),
          axios.get(`${apiUrl}/api/pos`)
        ]);

        console.log('📡 Raw API Responses:');
        console.log('  invoicesRes.data:', invoicesRes.data);
        console.log('  purchaseRes.data:', purchaseRes.data);

        // Handle invoices - can be array or object with data property
        let invoiceList = [];
        if (Array.isArray(invoicesRes.data)) {
          invoiceList = invoicesRes.data;
          console.log('✓ Invoices: Direct array');
        } else if (invoicesRes.data?.data) {
          invoiceList = Array.isArray(invoicesRes.data.data) ? invoicesRes.data.data : [];
          console.log('✓ Invoices: Found in .data property');
        }
        
        // Handle purchase orders - paginated response: purchaseRes.data.data.data
        let purchaseList = [];
        console.log('🔍 Checking purchaseRes.data structure:');
        console.log('  purchaseRes.data exists:', !!purchaseRes.data);
        console.log('  purchaseRes.data.data exists:', !!purchaseRes.data?.data);
        console.log('  purchaseRes.data.data.data exists:', !!purchaseRes.data?.data?.data);
        console.log('  purchaseRes.data.data.data is array:', Array.isArray(purchaseRes.data?.data?.data));
        console.log('  purchaseRes.data.data.data length:', purchaseRes.data?.data?.data?.length);
        
        // The API returns: { data: { current_page, data: [...] } }
        if (purchaseRes.data?.data?.data) {
          purchaseList = Array.isArray(purchaseRes.data.data.data) ? purchaseRes.data.data.data : [];
          console.log('✓ Purchase Orders: Found in .data.data.data property, extracted:', purchaseList.length);
        } else if (purchaseRes.data?.data && Array.isArray(purchaseRes.data.data)) {
          purchaseList = purchaseRes.data.data;
          console.log('✓ Purchase Orders: Found in .data.data property, extracted:', purchaseList.length);
        } else if (Array.isArray(purchaseRes.data)) {
          purchaseList = purchaseRes.data;
          console.log('✓ Purchase Orders: Direct array');
        } else {
          console.log('❌ Purchase Orders: Could not extract from response');
        }

        console.log('✓ Fetched data:');
        console.log(`  - Invoices: ${invoiceList.length}`);
        console.log(`  - Purchase Orders: ${purchaseList.length}`);
        invoiceList.forEach((inv, i) => {
          const ilineCount = inv.ilines ? inv.ilines.length : 0;
          console.log(`    Invoice[${i}]: ID=${inv.id}, ilines=${ilineCount}`);
        });
        purchaseList.forEach((po, i) => {
          const plineCount = po.plines ? po.plines.length : 0;
          console.log(`    PO[${i}]: ID=${po.id}, ref=${po.reference_no}, plines=${plineCount}`);
        });

        console.log('📌 About to set state:');
        console.log('  setInvoices with:', invoiceList.length);
        console.log('  setPurchaseInvoices with:', purchaseList.length);
        
        setInvoices(invoiceList);
        setPurchaseInvoices(purchaseList);
      } catch (err) {
        console.error('❌ Error fetching data:', err);
        setError('Unable to load data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [apiUrl]);

  // Filter invoices by date range and calculate VAT
  useEffect(() => {
    console.log('🔍 VAT Calculation triggered');
    console.log('  invoices:', invoices.length);
    console.log('  purchaseInvoices:', purchaseInvoices.length);
    console.log('  dateFrom:', dateFrom, 'dateTo:', dateTo);

    if (invoices.length === 0 && purchaseInvoices.length === 0) {
      console.log('  ⚠️  No data to process');
      setVatPayable(0);
      setVatReceivable(0);
      setNetVat(0);
      return;
    }

    // Filter and calculate VAT Payable (from sales invoices)
    const filteredInvoices = invoices.filter(inv => {
      const dateStr = inv.invoice_date || inv.createdAt;
      const inRange = isDateInRange(dateStr, dateFrom, dateTo);
      if (!inRange) {
        console.log(`  ❌ Invoice ${inv.id} filtered out - Date: ${dateStr}`);
      }
      return inRange;
    });

    const totalVatPayable = filteredInvoices.reduce((sum, inv) => {
      const vat = calculateInvoiceVAT(inv);
      console.log(`  📄 Invoice ${inv.id}: VAT = ${vat}`);
      return sum + vat;
    }, 0);

    // Filter and calculate VAT Receivable (from purchase invoices)
    console.log('Processing purchase invoices:');
    const filteredPurchases = purchaseInvoices.filter(inv => {
      const dateStr = inv.po_date || inv.purchase_date || inv.createdAt;
      const inRange = isDateInRange(dateStr, dateFrom, dateTo);
      console.log(`  PO ${inv.id} (${inv.reference_no}): date="${dateStr}", inRange=${inRange}, plines=${inv.plines ? inv.plines.length : 'MISSING'}`);
      if (!inRange) {
        console.log(`    ❌ FILTERED OUT - date not in range`);
      } else {
        console.log(`    ✅ INCLUDED`);
      }
      return inRange;
    });

    console.log(`\n📊 Total filtered POs: ${filteredPurchases.length}`);

    const totalVatReceivable = filteredPurchases.reduce((sum, inv) => {
      const vat = calculateInvoiceVAT(inv);
      console.log(`  📦 PO ${inv.id} (${inv.reference_no}): VAT = ${vat}`);
      return sum + vat;
    }, 0);

    console.log('✅ Calculation complete');
    console.log(`  Filtered Invoices: ${filteredInvoices.length}, Total VAT Payable: ${totalVatPayable}`);
    console.log(`  Filtered POs: ${filteredPurchases.length}, Total VAT Receivable: ${totalVatReceivable}`);

    setVatPayable(totalVatPayable);
    setVatReceivable(totalVatReceivable);
    setNetVat(totalVatPayable - totalVatReceivable);
  }, [dateFrom, dateTo, invoices, purchaseInvoices]);

  // Get filtered invoices for current date range
  const getFilteredInvoices = () => {
    return invoices.filter(inv => {
      const dateStr = inv.invoice_date || inv.createdAt;
      return isDateInRange(dateStr, dateFrom, dateTo);
    });
  };

  // Get filtered purchase invoices for current date range
  const getFilteredPurchases = () => {
    return purchaseInvoices.filter(inv => {
      const dateStr = inv.po_date || inv.purchase_date || inv.createdAt;
      return isDateInRange(dateStr, dateFrom, dateTo);
    });
  };

  // Export invoices to PDF
  const exportInvoicesPDF = async () => {
    const filteredList = getFilteredInvoices();
    
    const InvoicesPDFDocument = () => (
      <Document>
        <Page size="A4" orientation="landscape" style={pdfStyles.page}>
          <Text style={pdfStyles.title}>VAT Report - Sales Invoices</Text>
          <View style={{ marginBottom: 20 }}>
            <Text style={pdfStyles.subtitle}>
              Period: {formatDate(dateFrom)} - {formatDate(dateTo)}
            </Text>
            <Text style={{ fontSize: 10, color: '#0891b2', fontWeight: 'bold', marginTop: 5 }}>
              VAT Payable: {formatCurrency(vatPayable)}
            </Text>
          </View>

          <View style={pdfStyles.table}>
            <View style={pdfStyles.tableHeader}>
              <Text style={pdfStyles.col1}>Invoice #</Text>
              <Text style={pdfStyles.col2}>Customer</Text>
              <Text style={pdfStyles.col3}>Date</Text>
              <Text style={pdfStyles.col4}>Subtotal</Text>
              <Text style={pdfStyles.col5}>VAT</Text>
              <Text style={pdfStyles.col6}>Total</Text>
            </View>

            {filteredList.map((invoice) => {
              const vat = calculateInvoiceVAT(invoice);
              const total = calculateInvoiceTotal(invoice);
              const subtotal = total - vat;
              return (
                <View key={invoice.id} style={pdfStyles.tableRow}>
                  <Text style={pdfStyles.col1}>{invoice.invoice_number || invoice.invoice_no || invoice.id}</Text>
                  <Text style={pdfStyles.col2}>{invoice.customer?.name || invoice.customer_name || '-'}</Text>
                  <Text style={pdfStyles.col3}>{formatDate(invoice.invoice_date || invoice.createdAt)}</Text>
                  <Text style={pdfStyles.col4}>{formatCurrency(subtotal)}</Text>
                  <Text style={pdfStyles.col5}>{formatCurrency(vat)}</Text>
                  <Text style={pdfStyles.col6}>{formatCurrency(total)}</Text>
                </View>
              );
            })}

            {/* Totals Row */}
            <View style={{...pdfStyles.tableRow, backgroundColor: '#f3f4f6', fontWeight: 'bold', borderTopWidth: 2, borderTopColor: '#000'}}>
              <Text style={{...pdfStyles.col1, ...pdfStyles.col2, ...pdfStyles.col3, textAlign: 'right', paddingHorizontal: 3}}>TOTAL</Text>
              <Text style={{...pdfStyles.col4, fontWeight: 'bold'}}>
                {formatCurrency(filteredList.reduce((sum, inv) => sum + (calculateInvoiceTotal(inv) - calculateInvoiceVAT(inv)), 0))}
              </Text>
              <Text style={{...pdfStyles.col5, fontWeight: 'bold'}}>
                {formatCurrency(filteredList.reduce((sum, inv) => sum + calculateInvoiceVAT(inv), 0))}
              </Text>
              <Text style={{...pdfStyles.col6, fontWeight: 'bold'}}>
                {formatCurrency(filteredList.reduce((sum, inv) => sum + calculateInvoiceTotal(inv), 0))}
              </Text>
            </View>
          </View>

          <Text style={pdfStyles.footerText}>
            Generated on {new Date().toLocaleString('en-GB')}
          </Text>
        </Page>
      </Document>
    );

    try {
      const pdfDocument = React.createElement(InvoicesPDFDocument);
      const blob = await pdf(pdfDocument).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `VAT_Report_Invoices_${dateFrom}_to_${dateTo}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF export failed:', err);
      alert('Unable to export PDF. Please try again.');
    }
  };

  // Export purchase invoices to PDF
  const exportPurchasesPDF = async () => {
    const filteredList = getFilteredPurchases();
    
    const PurchasesPDFDocument = () => (
      <Document>
        <Page size="A4" orientation="landscape" style={pdfStyles.page}>
          <Text style={pdfStyles.title}>VAT Report - Purchase Invoices</Text>
          <View style={{ marginBottom: 20 }}>
            <Text style={pdfStyles.subtitle}>
              Period: {formatDate(dateFrom)} - {formatDate(dateTo)}
            </Text>
            <Text style={{ fontSize: 10, color: '#0891b2', fontWeight: 'bold', marginTop: 5 }}>
              VAT Receivable: {formatCurrency(vatReceivable)}
            </Text>
          </View>

          <View style={pdfStyles.table}>
            <View style={pdfStyles.tableHeader}>
              <Text style={pdfStyles.col1}>Invoice #</Text>
              <Text style={pdfStyles.col2}>Supplier</Text>
              <Text style={pdfStyles.col3}>Date</Text>
              <Text style={pdfStyles.col4}>Subtotal</Text>
              <Text style={pdfStyles.col5}>VAT</Text>
              <Text style={pdfStyles.col6}>Total</Text>
            </View>

            {filteredList.map((invoice) => {
              const vat = calculateInvoiceVAT(invoice);
              const total = calculateInvoiceTotal(invoice);
              const subtotal = total - vat;
              return (
                <View key={invoice.id} style={pdfStyles.tableRow}>
                  <Text style={pdfStyles.col1}>{invoice.reference_no || invoice.po_no || invoice.invoice_number || invoice.id}</Text>
                  <Text style={pdfStyles.col2}>{invoice.supplier?.name || invoice.supplier_name || invoice.customer_name || '-'}</Text>
                  <Text style={pdfStyles.col3}>{formatDate(invoice.po_date || invoice.purchase_date || invoice.createdAt)}</Text>
                  <Text style={pdfStyles.col4}>{formatCurrency(subtotal)}</Text>
                  <Text style={pdfStyles.col5}>{formatCurrency(vat)}</Text>
                  <Text style={pdfStyles.col6}>{formatCurrency(total)}</Text>
                </View>
              );
            })}

            {/* Totals Row */}
            <View style={{...pdfStyles.tableRow, backgroundColor: '#f3f4f6', fontWeight: 'bold', borderTopWidth: 2, borderTopColor: '#000'}}>
              <Text style={{...pdfStyles.col1, ...pdfStyles.col2, ...pdfStyles.col3, textAlign: 'right', paddingHorizontal: 3}}>TOTAL</Text>
              <Text style={{...pdfStyles.col4, fontWeight: 'bold'}}>
                {formatCurrency(filteredList.reduce((sum, inv) => sum + (calculateInvoiceTotal(inv) - calculateInvoiceVAT(inv)), 0))}
              </Text>
              <Text style={{...pdfStyles.col5, fontWeight: 'bold'}}>
                {formatCurrency(filteredList.reduce((sum, inv) => sum + calculateInvoiceVAT(inv), 0))}
              </Text>
              <Text style={{...pdfStyles.col6, fontWeight: 'bold'}}>
                {formatCurrency(filteredList.reduce((sum, inv) => sum + calculateInvoiceTotal(inv), 0))}
              </Text>
            </View>
          </View>

          <Text style={pdfStyles.footerText}>
            Generated on {new Date().toLocaleString('en-GB')}
          </Text>
        </Page>
      </Document>
    );

    try {
      const pdfDocument = React.createElement(PurchasesPDFDocument);
      const blob = await pdf(pdfDocument).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `VAT_Report_Purchases_${dateFrom}_to_${dateTo}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF export failed:', err);
      alert('Unable to export PDF. Please try again.');
    }
  };

  // PDF Styles
  const pdfStyles = StyleSheet.create({
    page: {
      padding: 20,
      fontFamily: 'Helvetica',
    },
    title: {
      fontSize: 16,
      fontWeight: 'bold',
      marginBottom: 10,
      color: '#0891b2',
    },
    subtitle: {
      fontSize: 9,
      color: '#666',
      marginBottom: 10,
    },
    table: {
      width: '100%',
      marginBottom: 20,
    },
    tableHeader: {
      flexDirection: 'row',
      backgroundColor: '#f3f4f6',
      fontWeight: 'bold',
      fontSize: 8,
      borderBottomWidth: 1,
      borderBottomColor: '#000',
      paddingVertical: 5,
    },
    tableRow: {
      flexDirection: 'row',
      fontSize: 7,
      borderBottomWidth: 1,
      borderBottomColor: '#e5e7eb',
      paddingVertical: 5,
    },
    col1: {
      width: '12%',
      paddingHorizontal: 3,
      overflow: 'hidden',
    },
    col2: {
      width: '20%',
      paddingHorizontal: 3,
      overflow: 'hidden',
    },
    col3: {
      width: '13%',
      paddingHorizontal: 3,
      overflow: 'hidden',
    },
    col4: {
      width: '15%',
      paddingHorizontal: 3,
      overflow: 'hidden',
    },
    col5: {
      width: '15%',
      paddingHorizontal: 3,
      overflow: 'hidden',
    },
    col6: {
      width: '15%',
      paddingHorizontal: 3,
      overflow: 'hidden',
    },
    footerText: {
      fontSize: 8,
      color: '#666',
      marginTop: 10,
    },
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center gap-4">
          <span className="loading loading-spinner loading-lg text-primary"></span>
          <p className="text-lg text-gray-600">Loading VAT Report...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-[#23A19B] rounded-t-2xl p-4 sm:p-8 text-white shadow-xl">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex-1">
              <h1 className="text-2xl sm:text-4xl font-bold mb-1 sm:mb-2">VAT Report</h1>
              <p className="text-xs sm:text-base text-white/80">Calculate and manage VAT payable and receivable</p>
            </div>
          </div>
        </div>

        {/* Date Range Selection */}
        <div className="bg-base-100 shadow-xl p-4 sm:p-6 border-t-4 border-primary">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            <div className="form-control">
              <label className="label">
                <span className="label-text font-semibold">Date From</span>
              </label>
              <input
                type="date"
                className="input input-bordered focus:border-primary"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>

            <div className="form-control">
              <label className="label">
                <span className="label-text font-semibold">Date To</span>
              </label>
              <input
                type="date"
                className="input input-bordered focus:border-primary"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
          </div>

          {/* Selected Date Range Display */}
          <div className="text-center text-sm text-gray-600 mt-2">
            <p>Selected Period: <span className="font-bold text-primary">{formatDate(dateFrom)} - {formatDate(dateTo)}</span></p>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="alert alert-error mt-4">
            <span>{error}</span>
          </div>
        )}

        {/* VAT Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          {/* VAT Payable Card */}
          <div className="card bg-gradient-to-br from-blue-50 to-blue-100 shadow-lg border-2 border-blue-200">
            <div className="card-body">
              <h2 className="card-title text-blue-900 text-lg">VAT Payable</h2>
              <p className="text-blue-600 text-xs mb-2">Sales VAT Collected</p>
              <div className="text-3xl font-bold text-blue-900">
                {formatCurrency(vatPayable)}
              </div>
              <p className="text-xs text-blue-600 mt-2">
                {getFilteredInvoices().length} invoices
              </p>
            </div>
          </div>

          {/* VAT Receivable Card */}
          <div className="card bg-gradient-to-br from-green-50 to-green-100 shadow-lg border-2 border-green-200">
            <div className="card-body">
              <h2 className="card-title text-green-900 text-lg">VAT Receivable</h2>
              <p className="text-green-600 text-xs mb-2">Purchase VAT Paid</p>
              <div className="text-3xl font-bold text-green-900">
                {formatCurrency(vatReceivable)}
              </div>
              <p className="text-xs text-green-600 mt-2">
                {getFilteredPurchases().length} invoices
              </p>
            </div>
          </div>

          {/* Net VAT Card */}
          <div className={`card bg-gradient-to-br ${netVat >= 0 ? 'from-orange-50 to-orange-100 border-orange-200' : 'from-purple-50 to-purple-100 border-purple-200'} shadow-lg border-2`}>
            <div className="card-body">
              <h2 className={`card-title text-lg ${netVat >= 0 ? 'text-orange-900' : 'text-purple-900'}`}>
                Net VAT
              </h2>
              <p className={`text-xs mb-2 ${netVat >= 0 ? 'text-orange-600' : 'text-purple-600'}`}>
                {netVat >= 0 ? 'VAT Due to Tax Authority' : 'VAT Refund Due'}
              </p>
              <div className={`text-3xl font-bold ${netVat >= 0 ? 'text-orange-900' : 'text-purple-900'}`}>
                {formatCurrency(Math.abs(netVat))}
              </div>
              <p className={`text-xs mt-2 ${netVat >= 0 ? 'text-orange-600' : 'text-purple-600'}`}>
                {netVat >= 0 ? 'Amount to pay' : 'Amount to receive'}
              </p>
            </div>
          </div>
        </div>

        {/* Tabs Section */}
        <div className="mt-6 bg-base-100 shadow-xl rounded-2xl overflow-hidden">
          {/* Tabs */}
          <div className="tabs tabs-boxed bg-base-200 p-4">
            <button
              onClick={() => setActiveTab('invoices')}
              className={`tab tab-lg ${activeTab === 'invoices' ? 'tab-active bg-primary text-white' : ''}`}
            >
              📋 Sales Invoices ({getFilteredInvoices().length})
            </button>
            <button
              onClick={() => setActiveTab('purchases')}
              className={`tab tab-lg ${activeTab === 'purchases' ? 'tab-active bg-primary text-white' : ''}`}
            >
              📦 Purchase Invoices ({getFilteredPurchases().length})
            </button>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {/* Sales Invoices Tab */}
            {activeTab === 'invoices' && (
              <div>
                <div className="mb-4 flex justify-end">
                  <button
                    onClick={exportInvoicesPDF}
                    disabled={getFilteredInvoices().length === 0}
                    className="btn btn-primary"
                  >
                    📄 Export as PDF
                  </button>
                </div>

                {getFilteredInvoices().length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500 text-lg">No invoices found for the selected date range</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="table w-full">
                      <thead className="bg-gradient-to-r from-primary to-secondary text-white">
                        <tr>
                          <th>Invoice #</th>
                          <th>Customer</th>
                          <th>Date</th>
                          <th>Subtotal</th>
                          <th>VAT (5%)</th>
                          <th>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getFilteredInvoices().map((invoice) => {
                          const vat = calculateInvoiceVAT(invoice);
                          const total = calculateInvoiceTotal(invoice);
                          const subtotal = total - vat;
                          return (
                            <tr key={invoice.id} className="hover:bg-base-200 transition-colors">
                              <td className="font-bold text-primary">{invoice.invoice_number || invoice.invoice_no || invoice.id}</td>
                              <td>{invoice.customer?.name || invoice.customer_name || '-'}</td>
                              <td>{formatDate(invoice.invoice_date || invoice.createdAt)}</td>
                              <td className="font-mono">{formatCurrency(subtotal)}</td>
                              <td className="font-mono text-blue-600 font-bold">{formatCurrency(vat)}</td>
                              <td className="font-mono font-bold">{formatCurrency(total)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot className="bg-gray-100 font-bold border-t-2 border-gray-300">
                        <tr>
                          <td colSpan="3" className="text-right pr-4">TOTAL</td>
                          <td className="font-mono">{formatCurrency(getFilteredInvoices().reduce((sum, inv) => sum + (calculateInvoiceTotal(inv) - calculateInvoiceVAT(inv)), 0))}</td>
                          <td className="font-mono text-blue-600">{formatCurrency(vatPayable)}</td>
                          <td className="font-mono">{formatCurrency(getFilteredInvoices().reduce((sum, inv) => sum + calculateInvoiceTotal(inv), 0))}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Purchase Invoices Tab */}
            {activeTab === 'purchases' && (
              <div>
                <div className="mb-4 flex justify-end">
                  <button
                    onClick={exportPurchasesPDF}
                    disabled={getFilteredPurchases().length === 0}
                    className="btn btn-primary"
                  >
                    📄 Export as PDF
                  </button>
                </div>

                {getFilteredPurchases().length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500 text-lg">No purchase invoices found for the selected date range</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="table w-full">
                      <thead className="bg-gradient-to-r from-primary to-secondary text-white">
                        <tr>
                          <th>Invoice #</th>
                          <th>Supplier</th>
                          <th>Date</th>
                          <th>Subtotal</th>
                          <th>VAT (5%)</th>
                          <th>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getFilteredPurchases().map((invoice) => {
                          const vat = calculateInvoiceVAT(invoice);
                          const total = calculateInvoiceTotal(invoice);
                          const subtotal = total - vat;
                          return (
                            <tr key={invoice.id} className="hover:bg-base-200 transition-colors">
                              <td className="font-bold text-primary">{invoice.reference_no || invoice.po_no || invoice.invoice_number || invoice.id}</td>
                              <td>{invoice.supplier?.name || invoice.supplier_name || invoice.customer_name || '-'}</td>
                              <td>{formatDate(invoice.po_date || invoice.purchase_date || invoice.createdAt)}</td>
                              <td className="font-mono">{formatCurrency(subtotal)}</td>
                              <td className="font-mono text-green-600 font-bold">{formatCurrency(vat)}</td>
                              <td className="font-mono font-bold">{formatCurrency(total)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot className="bg-gray-100 font-bold border-t-2 border-gray-300">
                        <tr>
                          <td colSpan="3" className="text-right pr-4">TOTAL</td>
                          <td className="font-mono">{formatCurrency(getFilteredPurchases().reduce((sum, inv) => sum + (calculateInvoiceTotal(inv) - calculateInvoiceVAT(inv)), 0))}</td>
                          <td className="font-mono text-green-600">{formatCurrency(vatReceivable)}</td>
                          <td className="font-mono">{formatCurrency(getFilteredPurchases().reduce((sum, inv) => sum + calculateInvoiceTotal(inv), 0))}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VatReport;
